import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { formatApiError } from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Loader2, Moon, Sun } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_agent-hub-177/artifacts/isc5pm97_Apollo%20Wide%20Logo.png";

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background" data-testid="login-page">
      {/* Theme toggle */}
      <button
        onClick={toggleTheme}
        className="absolute top-4 right-4 p-2 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors"
        data-testid="theme-toggle"
      >
        {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
      </button>

      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <img src={LOGO_URL} alt="Apollo" className="h-10 w-auto" />
          </div>
          
          <h1 className="text-3xl font-semibold tracking-tight mb-2 font-['Outfit']">Welcome back</h1>
          <p className="text-muted-foreground mb-8">Sign in to manage your AI agents</p>

          {error && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-500 px-4 py-3 rounded-lg mb-6 text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                className="rounded-lg"
                data-testid="login-email-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="rounded-lg"
                data-testid="login-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-gradient text-white rounded-lg h-11 font-medium"
              data-testid="login-submit-button"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sign in'}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Don't have an account?{' '}
            <Link to="/register" className="text-[#9333EA] hover:text-[#EC4899] transition-colors" data-testid="register-link">
              Create one
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Visual */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-[#9333EA] to-[#EC4899] items-center justify-center p-12">
        <div className="max-w-lg text-center text-white">
          <img src={LOGO_URL} alt="Apollo" className="h-16 w-auto mx-auto mb-8 brightness-0 invert" />
          <h2 className="text-2xl font-semibold mb-4 font-['Outfit']">Build AI Agents for Your Business</h2>
          <p className="text-white/80">
            Start with one strong business agent, then deploy it across web chat and SMS. Add more agents only when you need clearly different brands, audiences, or workflows.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
